import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import ScrollToTop from './Components/ScrollTop';
import DialBar from './Components/DialBar';
import Sidebar from './Components/Sidebar';
import Dashboard from './Pages/Dashboard';
import Calls from './Pages/Calls';
import Login from './Pages/Login';
import Dispositions from './Pages/Dispositions';
import Contacts from './Pages/Contacts';
import Users from './Pages/Users';
import Ticketing from './Pages/Ticketing';
import Profile from './Pages/Profile';
import Settings from './Pages/Settings';
import Monitor from './Pages/Monitor';
import Analyticz from './Pages/Analytics';
import Integrations from './Pages/Integrations';
import Scripts from './Pages/Scripts';
import Chat from './Pages/Chat';
import Faqs from './Pages/Faqs';
import Campaigns from './Pages/Campaigns';
import Forms from './Pages/Forms';
import Reminders from './Pages/Reminders';
import { createTheme, ThemeProvider, CssBaseline, Box } from '@mui/material';
import { DarkModeProvider, useDarkMode } from './Context/DarkMode';
import { WebSocketProvider } from './Context/WebSocket';

const Layout = ({ children }) => {
  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 6,
          bgcolor: 'background.main',
        }}
      >
        {children}
      </Box>
      <DialBar />
    </Box>
  );
};

const App = () => {
  const [userLevel, setUserLevel] = useState(sessionStorage.getItem("user_level"));
  const authToken = process.env.AUTH_TOKEN;


  useEffect(() => {
    const authyToken = localStorage.getItem(authToken);
    if (authyToken) {
      const decodedToken = jwtDecode(authyToken);
      const userLevel=localStorage.setItem('user_level',decodedToken.user_level);
      const expirationTime = decodedToken.exp * 1000;
      const currentTime = Date.now();
      if (currentTime > expirationTime) {
        localStorage.removeItem(authToken);
      } else {
        // setUserLevel(userLevel);
      }
    }
  
  }, []);

  // Utility function to check if the user is logged in
  const isLoggedIn = () => {
    const authToken = process.env.AUTH_TOKEN;
    const authyToken = localStorage.getItem(authToken);
    if (authyToken) {
      try {
        const decodedToken = jwtDecode(authyToken);
        const isTokenValid = decodedToken.exp * 1000 > Date.now();
        return isTokenValid;
      } catch {
        // Invalid token
        localStorage.removeItem(authyToken);
        return false;
      }
    }
    return false;
  };

  // PrivateRoute component to protect routes
  const PrivateRoute = ({ children }) => {
    return isLoggedIn() ? children : <Navigate to="/login" />;
  };

  return (
    <WebSocketProvider>
    <DarkModeProvider>
      <Router>
        <ThemeWithDarkMode>
          <CssBaseline />
          <ScrollToTop />
          <Routes>
            {/* Public route */}
            <Route path="/login" element={<Login />} />
            {/* Private routes for agents */}
            {userLevel === 'agent' && (
              <>
                <Route path="*" element={<Layout><Dashboard /></Layout>} />
                <Route path="/" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
                <Route path="/calls" element={<PrivateRoute><Layout><Calls /></Layout></PrivateRoute>} />
                <Route path="/dispositions" element={<PrivateRoute><Layout><Dispositions /></Layout></PrivateRoute>} />
                <Route path="/contacts" element={<PrivateRoute><Layout><Contacts /></Layout></PrivateRoute>} />
                <Route path="/ticketing" element={<PrivateRoute><Layout><Ticketing /></Layout></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
                <Route path="/chat" element={<PrivateRoute><Layout><Chat /></Layout></PrivateRoute>} />
                <Route path="/integrations" element={<PrivateRoute><Layout><Integrations /></Layout></PrivateRoute>} />
                <Route path="/scripts" element={<PrivateRoute><Layout><Scripts /></Layout></PrivateRoute>} />
                <Route path="/faqs" element={<PrivateRoute><Layout><Faqs /></Layout></PrivateRoute>} />
                <Route path="/campaigns" element={<PrivateRoute><Layout><Campaigns /></Layout></PrivateRoute>} />
                <Route path="/forms" element={<PrivateRoute><Layout><Forms /></Layout></PrivateRoute>} />
                <Route path="/reminders" element={<PrivateRoute><Layout><Reminders /></Layout></PrivateRoute>} />
                
              </>
            )}
            {/* Private routes for admin, superadmin, and supervisor */}
            {(userLevel === 'supervisor') && (
              <>
                <Route path="*" element={<Layout><Dashboard /></Layout>} />
                <Route path="/" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
                <Route path="/calls" element={<PrivateRoute><Layout><Calls /></Layout></PrivateRoute>} />
                <Route path="/dispositions" element={<PrivateRoute><Layout><Dispositions /></Layout></PrivateRoute>} />
                <Route path="/contacts" element={<PrivateRoute><Layout><Contacts /></Layout></PrivateRoute>} />
                <Route path="/users" element={<PrivateRoute><Layout><Users /></Layout></PrivateRoute>} />
                <Route path="/ticketing" element={<PrivateRoute><Layout><Ticketing /></Layout></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
                <Route path="/monitoring" element={<PrivateRoute><Layout><Monitor /></Layout></PrivateRoute>} />
                <Route path="/analytics" element={<PrivateRoute><Layout><Analyticz /></Layout></PrivateRoute>} />
                <Route path="/integrations" element={<PrivateRoute><Layout><Integrations /></Layout></PrivateRoute>} />
                <Route path="/chat" element={<PrivateRoute><Layout><Chat /></Layout></PrivateRoute>} />
                <Route path="/scripts" element={<PrivateRoute><Layout><Scripts /></Layout></PrivateRoute>} />
                <Route path="/faqs" element={<PrivateRoute><Layout><Faqs /></Layout></PrivateRoute>} />
                <Route path="/campaigns" element={<PrivateRoute><Layout><Campaigns /></Layout></PrivateRoute>} />
                <Route path="/forms" element={<PrivateRoute><Layout><Forms /></Layout></PrivateRoute>} />
                <Route path="/reminders" element={<PrivateRoute><Layout><Reminders /></Layout></PrivateRoute>} />
              </>
            )}
            {(userLevel === 'admin') && (
              <>
                <Route path="*" element={<Layout><Dashboard /></Layout>} />
                <Route path="/" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
                <Route path="/calls" element={<PrivateRoute><Layout><Calls /></Layout></PrivateRoute>} />
                <Route path="/dispositions" element={<PrivateRoute><Layout><Dispositions /></Layout></PrivateRoute>} />
                <Route path="/contacts" element={<PrivateRoute><Layout><Contacts /></Layout></PrivateRoute>} />
                <Route path="/users" element={<PrivateRoute><Layout><Users /></Layout></PrivateRoute>} />
                <Route path="/ticketing" element={<PrivateRoute><Layout><Ticketing /></Layout></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
                <Route path="/monitoring" element={<PrivateRoute><Layout><Monitor /></Layout></PrivateRoute>} />
                <Route path="/analytics" element={<PrivateRoute><Layout><Analyticz /></Layout></PrivateRoute>} />
                <Route path="/integrations" element={<PrivateRoute><Layout><Integrations /></Layout></PrivateRoute>} />
                <Route path="/chat" element={<PrivateRoute><Layout><Chat /></Layout></PrivateRoute>} />
                <Route path="/scripts" element={<PrivateRoute><Layout><Scripts /></Layout></PrivateRoute>} />
                <Route path="/faqs" element={<PrivateRoute><Layout><Faqs /></Layout></PrivateRoute>} />
                <Route path="/campaigns" element={<PrivateRoute><Layout><Campaigns /></Layout></PrivateRoute>} />
                <Route path="/forms" element={<PrivateRoute><Layout><Forms /></Layout></PrivateRoute>} />
                <Route path="/reminders" element={<PrivateRoute><Layout><Reminders /></Layout></PrivateRoute>} />
              </>
            )}
            {(userLevel === 'superadmin') && (
              <>
                <Route path="*" element={<Layout><Dashboard /></Layout>} />
                <Route path="/" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
                <Route path="/calls" element={<PrivateRoute><Layout><Calls /></Layout></PrivateRoute>} />
                <Route path="/dispositions" element={<PrivateRoute><Layout><Dispositions /></Layout></PrivateRoute>} />
                <Route path="/contacts" element={<PrivateRoute><Layout><Contacts /></Layout></PrivateRoute>} />
                <Route path="/users" element={<PrivateRoute><Layout><Users /></Layout></PrivateRoute>} />
                <Route path="/ticketing" element={<PrivateRoute><Layout><Ticketing /></Layout></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
                <Route path="/monitoring" element={<PrivateRoute><Layout><Monitor /></Layout></PrivateRoute>} />
                <Route path="/analytics" element={<PrivateRoute><Layout><Analyticz /></Layout></PrivateRoute>} />
                <Route path="/integrations" element={<PrivateRoute><Layout><Integrations /></Layout></PrivateRoute>} />
                <Route path="/chat" element={<PrivateRoute><Layout><Chat /></Layout></PrivateRoute>} />
                <Route path="/scripts" element={<PrivateRoute><Layout><Scripts /></Layout></PrivateRoute>} />
                <Route path="/faqs" element={<PrivateRoute><Layout><Faqs /></Layout></PrivateRoute>} />
                <Route path="/campaigns" element={<PrivateRoute><Layout><Campaigns /></Layout></PrivateRoute>} />
                <Route path="/forms" element={<PrivateRoute><Layout><Forms /></Layout></PrivateRoute>} />
                <Route path="/reminders" element={<PrivateRoute><Layout><Reminders /></Layout></PrivateRoute>} />
              </>
            )}
            {!userLevel && <Route path="*" element={<Navigate to="/login" />} />}
          </Routes>
        </ThemeWithDarkMode>
      </Router>
    </DarkModeProvider>
    </WebSocketProvider>
  );
};

const ThemeWithDarkMode = ({ children }) => {
  const { isDarkMode } = useDarkMode();

  const customTheme = createTheme({
    typography: {
      fontFamily: "'Roboto Condensed', sans-serif",
      fontSize: 12,
      h6: {
        fontSize: '0.9rem',
        fontWeight: 'bold',
      },
      body1: {
        fontSize: '0.8rem',
        fontWeight: 'bold',
      },
    },
    components: {
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: '35px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '8px',
          },
        },
      },
    },
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      background: {
        default: isDarkMode ? '#060A15' : '#f1f1f0',
        paper: isDarkMode ? '#151c26' : '#ffffff',
        appbar: isDarkMode ? '#01070f' : '#ffffff',
        sidebar: isDarkMode ? '#151c26' : '#ffffff',
        table: isDarkMode ? '#222d3d' : '#f1f1ff',
        chatrecipient: isDarkMode ? '#58565c' : '#d5dbde',
        chatsender: isDarkMode ? '#3083a1' : '#9fd0e3',
      },
    },
  });

  return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
};

export default App;